import React, { useState, useEffect, useCallback, useRef } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref as dbRef, get as dbGet } from 'firebase/database';
import { firestore, database } from '../firebaseConfig';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import { computeAndStoreSignals } from './leadSignalEngine';
import useResearchAccess from '../hooks/useResearchAccess';
import { recordCompanyRecord } from '../shared/recordIntelligenceLog';
import './SeoAnalyzerModal.css';

function normalizeDomain(raw) {
  if (!raw || typeof raw !== 'string') return '';
  let d = raw.trim().toLowerCase();
  d = d.replace(/^https?:\/\//, '');
  d = d.replace(/^www\./, '');
  d = d.split('/')[0];
  d = d.split('?')[0];
  return d.includes('.') ? d : '';
}

function formatNumber(n) {
  if (n == null) return '0';
  const num = Number(n);
  if (Number.isNaN(num)) return '0';
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toLocaleString();
}

function formatDecimal(n, digits = 2) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(digits);
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('de-DE', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---- Visibility trend line chart (dependency-free SVG) ------------------
// Renders the visibility index over time as a smooth area/line chart. Points
// arrive oldest→newest (12M … 1M … Jetzt) already sorted by the caller.
const SeoTrendChart = ({ points }) => {
  if (!Array.isArray(points) || points.length < 2) return null;

  const W = 620;
  const H = 190;
  const padL = 40;
  const padR = 14;
  const padTop = 16;
  const padBottom = 34;
  const innerW = W - padL - padR;
  const innerH = H - padTop - padBottom;

  const values = points.map(p => p.value);
  const rawMax = Math.max(...values);
  const rawMin = Math.min(...values);
  const range = rawMax - rawMin || Math.max(rawMax, 1);
  const top = rawMax + range * 0.18;
  const bottom = Math.max(0, rawMin - range * 0.18);
  const span = top - bottom || 1;

  const xAt = (i) => padL + (points.length === 1 ? innerW / 2 : (innerW * i) / (points.length - 1));
  const yAt = (v) => padTop + innerH * (1 - (v - bottom) / span);

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p.value).toFixed(1)}`)
    .join(' ');
  const areaPath =
    `${linePath} L ${xAt(points.length - 1).toFixed(1)} ${(padTop + innerH).toFixed(1)}` +
    ` L ${xAt(0).toFixed(1)} ${(padTop + innerH).toFixed(1)} Z`;

  // Show at most ~8 x-axis labels so they never collide.
  const labelStep = Math.max(1, Math.ceil(points.length / 8));

  return (
    <div className="sar-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="sar-chart-svg" role="img" aria-label="Sichtbarkeits-Trend">
        <defs>
          <linearGradient id="sarTrendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--blue)" stopOpacity="0.30" />
            <stop offset="100%" stopColor="var(--blue)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0, 0.5, 1].map((t) => {
          const gy = padTop + innerH * t;
          const val = top - span * t;
          return (
            <g key={t}>
              <line x1={padL} y1={gy} x2={W - padR} y2={gy} stroke="#eef1f5" strokeWidth="1" />
              <text x={padL - 8} y={gy + 3.5} className="sar-chart-axis" textAnchor="end">{val.toFixed(1)}</text>
            </g>
          );
        })}

        <path d={areaPath} fill="url(#sarTrendFill)" />
        <path d={linePath} fill="none" stroke="var(--blue)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

        {points.map((p, i) => {
          const showLabel = p.isNow || i % labelStep === 0;
          return (
            <g key={i}>
              <circle
                cx={xAt(i)}
                cy={yAt(p.value)}
                r={p.isNow ? 4.5 : 3}
                fill={p.isNow ? 'var(--darkblue)' : '#ffffff'}
                stroke="var(--blue)"
                strokeWidth="2"
              />
              {showLabel && (
                <text x={xAt(i)} y={H - 12} className={`sar-chart-xlabel ${p.isNow ? 'is-now' : ''}`} textAnchor="middle">
                  {p.axisLabel}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ---- Ranking distribution bar chart -------------------------------------
// Each bar = number of keywords ranking on a given Google SERP page.
// Page 1 = positions 1–10, page 2 = 11–20, … page 10 = 91–100.
const SeoRankingBars = ({ rd }) => {
  const pages = [];
  for (let i = 1; i <= 10; i++) {
    const key = `page${String(i).padStart(2, '0')}`;
    if (rd[key] != null) {
      pages.push({ page: i, count: Number(rd[key]) || 0, range: `${(i - 1) * 10 + 1}–${i * 10}` });
    }
  }
  if (!pages.length) return null;
  const max = Math.max(...pages.map(p => p.count), 1);

  return (
    <div className="sar-bars">
      {pages.map((p) => (
        <div
          key={p.page}
          className={`sar-bar-col ${p.page === 1 ? 'is-page1' : ''}`}
          title={`${p.count} Keywords auf Google-Seite ${p.page} (Position ${p.range})`}
        >
          <div className="sar-bar-value">{formatNumber(p.count)}</div>
          <div className="sar-bar-track">
            <div className="sar-bar-fill" style={{ height: `${Math.max(3, (p.count / max) * 100)}%` }} />
          </div>
          <div className="sar-bar-label">Seite {p.page}</div>
          <div className="sar-bar-sublabel">Pos {p.range}</div>
        </div>
      ))}
    </div>
  );
};

// Horizontal meter bar used in competitor / opportunity rows.
const SeoMeter = ({ pct }) => (
  <div className="sar-meter">
    <div className="sar-meter-fill" style={{ width: `${Math.max(4, Math.min(100, pct))}%` }} />
  </div>
);

// Buckets Sistrix' competition score (0–1, sometimes 0–100) into a plain-language
// level. Low competition + already on page 2 = the easiest, fastest SEO win.
function competitionInfo(raw) {
  let v = Number(raw);
  if (Number.isNaN(v) || v <= 0) return null;
  if (v > 1) v = v / 100;
  if (v < 0.34) return { label: 'niedrig', cls: 'low' };
  if (v < 0.67) return { label: 'mittel', cls: 'mid' };
  return { label: 'hoch', cls: 'high' };
}

const DEFAULT_SETTINGS = {
  activeModules: ['coreMetrics'],
  country: 'de',
  limits: { competitors: 3, opportunities: 5 },
};

const SeoAnalyzerModal = ({ isOpen, onClose, contactData, userId, onContactUpdate }) => {
  const { research, researchLoading } = useResearchAccess();
  const locked = !researchLoading && research.hasSeoAnalyzer !== true;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [source, setSource] = useState(null);
  const settingsRef = useRef(DEFAULT_SETTINGS);

  const rawWebsite = contactData?.lead_website || contactData?.website || '';
  const domain = normalizeDomain(rawWebsite);
  const contactId = contactData?.id;

  // ---- Saved data loader --------------------------------------------------
  const loadSaved = useCallback(async () => {
    if (!userId || !contactId) return null;
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
      const snap = await getDoc(contactRef);
      if (!snap.exists()) return null;
      const ri = snap.data()?.researchIntelligence;
      if (!ri?.seoAnalysis?.data) return null;
      return ri.seoAnalysis;
    } catch (err) {
      console.error('[SeoAnalyzer] Load saved failed:', err);
      return null;
    }
  }, [userId, contactId]);

  // ---- User's configured scan settings ------------------------------------
  const loadSettings = useCallback(async () => {
    if (!userId) return DEFAULT_SETTINGS;
    try {
      const snap = await dbGet(dbRef(database, `World/${userId}/settings/seoAnalyzerSettings`));
      if (!snap.exists()) return DEFAULT_SETTINGS;
      const s = snap.val() || {};
      return {
        activeModules: Array.isArray(s.activeModules) && s.activeModules.length
          ? s.activeModules
          : DEFAULT_SETTINGS.activeModules,
        country: s.country || DEFAULT_SETTINGS.country,
        limits: {
          competitors: s.limits?.competitors ?? DEFAULT_SETTINGS.limits.competitors,
          opportunities: s.limits?.opportunities ?? DEFAULT_SETTINGS.limits.opportunities,
        },
      };
    } catch (err) {
      console.warn('[SeoAnalyzer] Load settings failed, using defaults:', err);
      return DEFAULT_SETTINGS;
    }
  }, [userId]);

  // ---- Persist to lead ----------------------------------------------------
  const saveToFirestore = useCallback(async (scan) => {
    if (!userId || !contactId) return;
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);

      // Compact description block (analog zum IG-Muster)
      const bits = [];
      const core = scan?.data?.core;
      if (core?.visibility?.value != null) {
        bits.push(`Sichtbarkeitsindex ${formatDecimal(core.visibility.value)}`);
      }
      if (core?.keywordsTotal != null) {
        bits.push(`${formatNumber(core.keywordsTotal)} Keywords`);
      }
      if (core?.keywordsTop10 != null) {
        bits.push(`${formatNumber(core.keywordsTop10)} in Top 10`);
      }
      const tv = scan?.data?.trafficValue;
      if (tv?.estimatedTrafficMonthly != null) {
        bits.push(`~${formatNumber(tv.estimatedTrafficMonthly)} Besucher/Monat`);
      }
      if (tv?.estimatedValueMonthly) {
        bits.push(`Traffic-Wert ~${formatNumber(tv.estimatedValueMonthly)} €`);
      }
      const vt = scan?.data?.visibilityTrend;
      if (vt?.changePct != null) {
        const arrow = vt.direction === 'up' ? '↑' : vt.direction === 'down' ? '↓' : '→';
        bits.push(`Sichtbarkeit ${vt.changePct > 0 ? '+' : ''}${vt.changePct}% (${vt.overWindow || '12M'}) ${arrow}`);
      }
      const topCompetitor = Array.isArray(scan?.data?.competitors) && scan.data.competitors[0];
      if (topCompetitor?.domain) bits.push(`Top-Wettbewerber: ${topCompetitor.domain}`);
      const backlinks = scan?.data?.backlinks;
      if (backlinks?.total != null) bits.push(`${formatNumber(backlinks.total)} Backlinks`);
      const topOpp = Array.isArray(scan?.data?.opportunities) && scan.data.opportunities[0];
      if (topOpp?.keyword) bits.push(`Top-Chance: "${topOpp.keyword}" (Pos ${topOpp.position})`);

      const seoBlock = bits.length ? bits.join(' · ') : '';
      const existingDesc = contactData?.lead_description || '';
      const descClean = existingDesc.replace(/\n?\[SEO\].*$/s, '').trim();
      const newDesc = seoBlock
        ? (descClean ? `${descClean}\n[SEO] ${seoBlock}` : `[SEO] ${seoBlock}`)
        : descClean;

      const seoAnalysis = {
        domain: scan.domain,
        country: scan.country,
        scannedAt: scan.scannedAt,
        activeModules: scan.activeModules || [],
        data: scan.data || {},
        sistrixCreditsUsed: scan.sistrixCreditsUsed || 0,
      };

      const payload = {
        'researchIntelligence.seoAnalysis': seoAnalysis,
        lead_description: newDesc,
      };

      if (core?.visibility?.value != null) payload.lead_visibility_index = Number(core.visibility.value);
      if (core?.keywordsTotal != null) payload.lead_seo_keywords_total = Number(core.keywordsTotal);
      if (core?.keywordsTop10 != null) payload.lead_seo_keywords_top10 = Number(core.keywordsTop10);

      await updateDoc(contactRef, payload);
      await recordCompanyRecord(userId, contactId, 'seo', contactData, {
        ...contactData,
        researchIntelligence: {
          ...(contactData?.researchIntelligence || {}),
          seoAnalysis,
        },
      });

      // Patch the parent's locally-cached row so the freshly-scanned SEO data
      // shows up instantly (research tabs + description) without a full reload —
      // the table loads contacts once via getDocs, so it won't refresh itself.
      if (typeof onContactUpdate === 'function') {
        const localUpdates = {
          researchIntelligence: {
            ...(contactData?.researchIntelligence || {}),
            seoAnalysis,
          },
          lead_description: newDesc,
        };
        if (core?.visibility?.value != null) localUpdates.lead_visibility_index = Number(core.visibility.value);
        if (core?.keywordsTotal != null) localUpdates.lead_seo_keywords_total = Number(core.keywordsTotal);
        if (core?.keywordsTop10 != null) localUpdates.lead_seo_keywords_top10 = Number(core.keywordsTop10);
        onContactUpdate(contactId, localUpdates);
      }

      try {
        const freshData = { ...contactData, ...payload, lead_description: newDesc };
        computeAndStoreSignals(userId, contactId, freshData, { force: true }).catch(() => {});
      } catch (_) { /* non-fatal */ }
    } catch (saveErr) {
      console.error('[SeoAnalyzer] Save failed:', saveErr);
    }
  }, [userId, contactId, contactData, onContactUpdate]);

  // ---- Fresh fetch --------------------------------------------------------
  // No internal credit deduction — the account scans with its own Sistrix API
  // key, so cost is measured purely in Sistrix credits (tracked on the lead).
  const fetchFresh = useCallback(async () => {
    if (!domain) return;
    // Bestehende Analysen bleiben lesbar; ein neuer Scan braucht die Lizenz.
    if (locked) {
      setError('SEO Analyzer is not available for your account.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const settings = settingsRef.current || DEFAULT_SETTINGS;
      const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.sistrixProxy(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Active World id (owner uid for team members) — lets the backend
          // load the OWNER's Sistrix key so every team member can scan.
          userId,
          domain,
          country: settings.country,
          activeModules: settings.activeModules,
          limits: settings.limits,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || errData.message || `HTTP ${res.status}`);
      }

      const json = await res.json();
      if (!json.success) {
        throw new Error(json.error || 'SEO-Scan fehlgeschlagen');
      }

      setData(json);
      setSource('fresh');
      await saveToFirestore(json);
    } catch (err) {
      setError(err.message || 'SEO-Scan fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [domain, locked, userId, saveToFirestore]);

  // ---- Init on open -------------------------------------------------------
  useEffect(() => {
    if (!isOpen || !domain) return;
    let cancelled = false;

    const init = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      setSource(null);

      settingsRef.current = await loadSettings();
      if (cancelled) return;

      const saved = await loadSaved();
      if (cancelled) return;

      if (saved) {
        setData(saved);
        setSource('saved');
        setLoading(false);
      } else {
        await fetchFresh();
      }
    };

    init();
    return () => { cancelled = true; };
  }, [isOpen, domain, loadSaved, loadSettings, fetchFresh]);

  if (!isOpen) return null;

  if (!domain) {
    return (
      <div className="sar-overlay" onClick={onClose}>
        <div className="sar-modal" onClick={e => e.stopPropagation()}>
          <div className="sar-header">
            <h3 className="sar-title">SEO Analyzer</h3>
            <button className="sar-close" onClick={onClose}><i className="fa fa-times" /></button>
          </div>
          <div className="sar-error">
            <i className="fa fa-exclamation-circle" />
            <span>Kein Website-Link für diesen Kontakt hinterlegt.</span>
          </div>
        </div>
      </div>
    );
  }

  const core = data?.data?.core;
  const rd = data?.data?.rankingDistribution;
  const backlinks = data?.data?.backlinks;
  const trafficValue = data?.data?.trafficValue;
  const visibilityTrend = data?.data?.visibilityTrend;
  const competitors = data?.data?.competitors || [];
  const opportunities = data?.data?.opportunities || [];

  const scannedAt = data?.scannedAt ? new Date(data.scannedAt) : null;
  const visibilityVal = core?.visibility?.value;

  // Build chronological trend points (oldest → newest) and append the current
  // value as "Jetzt" so the chart always ends on today's visibility.
  const trendPoints = (() => {
    const raw = Array.isArray(visibilityTrend?.points) ? visibilityTrend.points : [];
    const pts = raw
      .map(p => ({ months: parseInt(p.label, 10) || 0, value: Number(p.value) }))
      .filter(p => !Number.isNaN(p.value))
      .sort((a, b) => b.months - a.months)
      .map(p => ({ value: p.value, axisLabel: `${p.months}M`, isNow: false }));
    const current = visibilityTrend?.current != null
      ? Number(visibilityTrend.current)
      : (visibilityVal != null ? Number(visibilityVal) : null);
    if (current != null && !Number.isNaN(current)) {
      pts.push({ value: current, axisLabel: 'Jetzt', isNow: true });
    }
    return pts;
  })();
  const hasTrend = trendPoints.length >= 2;

  const maxCompMatch = competitors.length ? Math.max(...competitors.map(c => Number(c.match) || 0), 0.0001) : 1;
  const maxOppGain = opportunities.length ? Math.max(...opportunities.map(o => Number(o.gain) || 0), 1) : 1;

  return (
    <div className="sar-overlay" onClick={onClose}>
      <div className="sar-modal" onClick={e => e.stopPropagation()}>

        <div className="sar-header">
          <div className="sar-header-left">
            <a
              href={`https://${domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="sar-header-icon"
              title="Webseite öffnen"
            >
              <i className="fa fa-magnifying-glass-chart" />
            </a>
            <div className="sar-header-info">
              <h3 className="sar-title">
                <a
                  href={`https://${domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sar-title-link"
                >
                  {domain} <i className="fa fa-external-link-alt" />
                </a>
              </h3>
              <span className="sar-sub">SEO Analyzer · {data?.country?.toUpperCase() || 'DE'}</span>
            </div>
          </div>
          <button className="sar-close" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        {loading && (
          <div className="sar-loading">
            <div className="sar-loading-icon">
              <i className="fa fa-magnifying-glass-chart" />
              <div className="sar-loading-ring" />
            </div>
            <span className="sar-loading-text">SEO-Daten werden abgerufen…</span>
          </div>
        )}

        {error && !loading && (
          <div className="sar-error">
            <i className="fa fa-exclamation-circle" />
            <span>{error}</span>
          </div>
        )}

        {data && !loading && (
          <div className="sar-body">

            {source === 'saved' && (
              <div className="sar-source-bar">
                <span className="sar-source-text">
                  <i className="fa fa-database" />
                  Gespeichert{scannedAt ? ` · ${timeAgo(scannedAt.toISOString())}` : ''}
                </span>
                <button className="sar-refresh-btn" onClick={fetchFresh} disabled={loading}>
                  <i className="fa fa-sync-alt" />
                  Neu scannen
                </button>
              </div>
            )}

            {source === 'fresh' && (
              <div className="sar-saved-hint">
                <i className="fa fa-check-circle" />
                <span>
                  SEO-Daten gespeichert
                  {data?.sistrixCreditsUsed != null ? ` · ${data.sistrixCreditsUsed} Sistrix-Credits` : ''}
                </span>
              </div>
            )}

            {/* ── Core Metrics ── */}
            {core && (
              <div className="sar-section">
                <h4 className="sar-section-title">
                  <i className="fa fa-chart-line" /> Core Metrics
                </h4>
                <p className="sar-section-note">
                  Grund­gesundheit der Domain bei Google: Gesamt­sichtbarkeit und Keyword-Stärke.
                </p>
                <div className="sar-metrics">
                  <div className="sar-metric">
                    <div className="sar-metric-label">Sichtbarkeitsindex</div>
                    <div className="sar-metric-value">{formatDecimal(visibilityVal)}</div>
                    {core.visibility?.date && (
                      <div className="sar-metric-sub">{core.visibility.date}</div>
                    )}
                  </div>
                  <div className="sar-metric">
                    <div className="sar-metric-label">Keywords gesamt</div>
                    <div className="sar-metric-value">{formatNumber(core.keywordsTotal)}</div>
                    <div className="sar-metric-sub">Rankings bei Google</div>
                  </div>
                  <div className="sar-metric">
                    <div className="sar-metric-label">In Top 10</div>
                    <div className="sar-metric-value">{formatNumber(core.keywordsTop10)}</div>
                    <div className="sar-metric-sub">auf Seite 1</div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Visibility Trend (line chart) ── */}
            {hasTrend && (
              <div className="sar-section">
                <h4 className="sar-section-title">
                  <i className="fa fa-arrow-trend-up" /> Sichtbarkeits-Trend
                  {visibilityTrend?.changePct != null && (
                    <span className={`sar-trend-badge ${visibilityTrend.direction || 'flat'}`}>
                      {visibilityTrend.direction === 'up' ? '↑' : visibilityTrend.direction === 'down' ? '↓' : '→'}
                      {' '}{visibilityTrend.changePct > 0 ? '+' : ''}{visibilityTrend.changePct}%
                      <span className="sar-trend-badge-window">({visibilityTrend.overWindow || '12M'})</span>
                    </span>
                  )}
                </h4>
                <p className="sar-section-note">
                  Entwicklung des Sichtbarkeitsindex über die letzten Monate bis heute. Fallende Kurven = heißer Lead.
                </p>
                <SeoTrendChart points={trendPoints} />
              </div>
            )}

            {/* ── Ranking Distribution (bar chart) ── */}
            {rd && (
              <div className="sar-section">
                <h4 className="sar-section-title">
                  <i className="fa fa-list-ol" /> Ranking-Verteilung
                </h4>
                <p className="sar-section-note">
                  So verteilen sich die Keywords, für die <strong>{domain}</strong> rankt, über die
                  Google-Ergebnisseiten (nicht die Unterseiten der Website). Seite 1 = Positionen 1–10,
                  Seite 2 = 11–20 usw. Je mehr auf Seite 1, desto besser.
                </p>
                <SeoRankingBars rd={rd} />
              </div>
            )}

            {/* ── Traffic & Value ── */}
            {trafficValue && (trafficValue.estimatedTrafficMonthly != null || trafficValue.estimatedValueMonthly) && (
              <div className="sar-section">
                <h4 className="sar-section-title">
                  <i className="fa fa-euro-sign" /> Traffic &amp; Wert
                </h4>
                <p className="sar-section-note">
                  Geschätzter organischer Google-Traffic pro Monat und dessen Werbewert in Euro.
                </p>
                <div className="sar-metrics">
                  {trafficValue.estimatedTrafficMonthly != null && (
                    <div className="sar-metric">
                      <div className="sar-metric-label">Besucher / Monat</div>
                      <div className="sar-metric-value">~{formatNumber(trafficValue.estimatedTrafficMonthly)}</div>
                      <div className="sar-metric-sub">geschätzt (organisch)</div>
                    </div>
                  )}
                  {trafficValue.estimatedValueMonthly ? (
                    <div className="sar-metric">
                      <div className="sar-metric-label">Traffic-Wert</div>
                      <div className="sar-metric-value">~{formatNumber(trafficValue.estimatedValueMonthly)} €</div>
                      <div className="sar-metric-sub">pro Monat</div>
                    </div>
                  ) : null}
                  {trafficValue.topPath?.keywords != null && (
                    <div className="sar-metric">
                      <div className="sar-metric-label">Stärkste Seite</div>
                      <div className="sar-metric-value">{formatNumber(trafficValue.topPath.keywords)}</div>
                      <div className="sar-metric-sub sar-metric-sub--path">{trafficValue.topPath.path || '/'}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Backlinks ── */}
            {backlinks && (
              <div className="sar-section">
                <h4 className="sar-section-title">
                  <i className="fa fa-link" /> Backlinks
                </h4>
                <p className="sar-section-note">
                  Größe und Vielfalt des Linkprofils — ein zentrales Vertrauens-Signal für Google.
                </p>
                <div className="sar-metrics">
                  <div className="sar-metric">
                    <div className="sar-metric-label">Backlinks gesamt</div>
                    <div className="sar-metric-value">{formatNumber(backlinks.total)}</div>
                  </div>
                  {backlinks.referringDomains != null && (
                    <div className="sar-metric">
                      <div className="sar-metric-label">Verweisende Domains</div>
                      <div className="sar-metric-value">{formatNumber(backlinks.referringDomains)}</div>
                    </div>
                  )}
                  {backlinks.hosts != null && (
                    <div className="sar-metric">
                      <div className="sar-metric-label">Verweisende Hosts</div>
                      <div className="sar-metric-value">{formatNumber(backlinks.hosts)}</div>
                    </div>
                  )}
                  {backlinks.classCNetworks != null && (
                    <div className="sar-metric">
                      <div className="sar-metric-label">Class-C-Netze</div>
                      <div className="sar-metric-value">{formatNumber(backlinks.classCNetworks)}</div>
                      <div className="sar-metric-sub">Netzwerk-Vielfalt</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Competitors ── */}
            {competitors.length > 0 && (
              <div className="sar-section">
                <h4 className="sar-section-title">
                  <i className="fa fa-users" /> Top-Wettbewerber
                </h4>
                <p className="sar-section-note">
                  Domains mit der größten Keyword-Überschneidung — Marktplätze &amp; Portale sind herausgefiltert.
                </p>
                <div className="sar-list">
                  {competitors.map((c, i) => (
                    <div key={c.domain + i} className="sar-rank-row">
                      <span className="sar-rank-index">{i + 1}</span>
                      <span className="sar-rank-main">
                        <a href={`https://${c.domain}`} target="_blank" rel="noopener noreferrer">
                          {c.domain}
                        </a>
                      </span>
                      <SeoMeter pct={((Number(c.match) || 0) / maxCompMatch) * 100} />
                      <span className="sar-rank-side">Match {formatDecimal(c.match, 2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Opportunities ── */}
            {opportunities.length > 0 && (
              <div className="sar-section">
                <h4 className="sar-section-title">
                  <i className="fa fa-bullseye" /> Keyword-Chancen
                </h4>
                <p className="sar-section-note">
                  Keywords, für die <strong>{domain}</strong> bei Google schon auf <strong>Seite 2</strong> rankt
                  (Position 11–20) — also knapp an Seite 1 vorbei. Genau hier steckt das größte ungenutzte
                  Potenzial: Schon kleine SEO-Verbesserungen können diese Begriffe auf Seite 1 heben und
                  spürbar mehr Besucher bringen. Am schnellsten gelingt das bei niedrigem Wettbewerb.
                </p>
                <div className="sar-legend">
                  <span className="sar-legend-item"><span className="sar-legend-key">Platz</span> aktuelle Google-Position</span>
                  <span className="sar-legend-item"><span className="sar-legend-key">Wettbewerb</span> wie umkämpft das Keyword ist</span>
                  <span className="sar-legend-item"><span className="sar-legend-key">Potenzial</span> Traffic-Zuwachs bei Sprung auf Seite 1</span>
                </div>
                <div className="sar-list">
                  {opportunities.map((o, i) => {
                    const comp = competitionInfo(o.competition);
                    return (
                      <div key={o.keyword + i} className="sar-rank-row sar-rank-row--opp">
                        <span className="sar-rank-pos" title={`Aktuelle Google-Position: Platz ${o.position}`}>
                          Platz {o.position}
                        </span>
                        <span className="sar-rank-main">
                          <strong>{o.keyword}</strong>
                          {o.url && (
                            <a href={o.url} target="_blank" rel="noopener noreferrer" className="sar-list-link">
                              <i className="fa fa-external-link-alt" />
                            </a>
                          )}
                        </span>
                        {comp ? (
                          <span className={`sar-comp-chip ${comp.cls}`} title="Wettbewerb um dieses Keyword">
                            Wettbewerb {comp.label}
                          </span>
                        ) : <span />}
                        <SeoMeter pct={((Number(o.gain) || 0) / maxOppGain) * 100} />
                        <span className="sar-rank-side" title="Geschätztes Traffic-Potenzial">
                          Potenzial {formatNumber(o.gain)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {data?.errors && Object.keys(data.errors).length > 0 && (
              <div className="sar-warnings">
                <i className="fa fa-exclamation-triangle" />
                Einige Module konnten nicht abgerufen werden:
                {' '}{Object.keys(data.errors).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SeoAnalyzerModal;
